import React, { useState } from 'react';
import { NavLink } from '../types';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import { useToast } from '../contexts/ToastContext';
import { submitContactForm } from '../services/contactService';
import './ContactPage.css';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSuccess, showError } = useToast();

  const navLinks: NavLink[] = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '/contact' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email.trim()) {
      showError('Validation Error', 'Email is required');
      return;
    }

    if (!formData.message.trim()) {
      showError('Validation Error', 'Message is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactForm(formData);
      showSuccess('Message Sent!', 'Thank you for your message! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      showError('Submission Error', 'Sorry, there was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = (email: string): void => {
    console.log('Email subscription:', email);
    // Handle email subscription logic here
  };

  const handleFollowShop = (): void => {
    console.log('Follow on shop clicked');
    // Handle follow shop logic here
  };

  return (
    <div className="contact-page">
      <Header navLinks={navLinks} />
      
      <main className="contact-main">
        <div className="contact-container">
          <div className="contact-header">
            <h1>Get in Touch</h1>
            <p>
              Have a question about our cakes? Want to discuss a custom order? 
              We'd love to hear from you! Send us a message and we'll get back to you as soon as possible.
            </p>
          </div>

          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-info-section">
                <h3>Contact Information</h3>
                <div className="contact-details">
                  <div className="contact-detail">
                    <strong>Email:</strong>
                    <a href="mailto:thefrostingroom@icloud.com">thefrostingroom@icloud.com</a>
                    <strong>Phone:</strong>
                    <a href="tel:+353899475387">+353 089 947 5387</a>
                  </div>
                  <div className="contact-detail">
                    <strong>Location:</strong>
                    <span>Dublin, Ireland</span>
                  </div>
                  <div className="contact-detail">
                    <strong>Response Time:</strong>
                    <span>We typically respond within 24 hours</span>
                  </div>
                </div>
              </div>

              <div className="contact-info-section">
                <h3>What We Can Help With</h3>
                <ul className="help-list">
                  <li>Custom cake orders and consultations</li>
                  <li>Wedding cake inquiries</li>
                  <li>Special dietary requirements</li>
                  <li>Delivery and collection options</li>
                  <li>Pricing and availability</li>
                  <li>General questions about our products</li>
                </ul>
              </div>
            </div>

            <div className="contact-form-section">
              <form className="contact-form" onSubmit={handleSubmit}>
                <h3>Send us a Message</h3>
                
                <div className="form-row">
                  <Input
                    type="text"
                    name="name"
                    label="Name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-input">
                  <Input
                    type="text"
                    name="phone"
                    label="Phone Number"
                    placeholder="+353 1 234 5678"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-input">
                  <Input
                    type="textarea"
                    name="message"
                    label="Message"
                    placeholder="Tell us about your cake requirements, questions, or anything else you'd like to discuss..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="form-input form-textarea"
                  />
                </div>

                <div className="form-actions">
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    disabled={isSubmitting}
                    className="submit-button"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

export default ContactPage;
